import { createBrowserRouter, type RouteObject } from 'react-router';
import { AppShell } from './components/layout/AppShell';
import { RequireAuth } from './components/RequireAuth';
import { HUBS, LEGACY_REDIRECTS, MOVED_TABS } from './nav/hubs';
import { ACCOUNTS_ROUTES } from './pages/accounts';
import { COMMERCE_ROUTES } from './pages/commerce';
import { REGISTRY_ROUTES } from './pages/registry';
import { MARKET_ROUTES } from './pages/market';
import { ADMIN_ROUTES } from './pages/admin';
import { CORE_ROUTES } from './pages/core';
import { GOVERNANCE_ROUTES } from './pages/governance';
import { INTEGRATIONS_ROUTES } from './pages/integrations';
import { WORK_ROUTES } from './pages/work';
import { SERVICES_ROUTES } from './pages/services';
import { OPS_ROUTES } from './pages/ops';
import { AbuseCasePage } from './pages/compliance/AbuseCasePage';
import { DisclosureCasePage } from './pages/compliance/DisclosureCasePage';
import { DisputePage } from './pages/compliance/DisputePage';
import { DomainDetailPage } from './pages/domains/DomainDetailPage';
import { NotFoundPage, RouteErrorPage } from './pages/ErrorPages';
import { HubPage } from './pages/hubs/HubPage';
import { LegacyRedirect } from './pages/hubs/LegacyRedirect';
import { LoginPage } from './pages/LoginPage';
import { CorrelationPage } from './pages/ops/CorrelationPage';
import { TracePage } from './pages/ops/TracePage';
import { RegistryTransactionPage } from './pages/registry/RegistryTransactionPage';
import { SearchPage } from './pages/SearchPage';
import { SubjectPage } from './pages/subjects/SubjectView';

/** The eight hubs (`nav/hubs.ts`); `/` is the home hub. `key` remounts when moving between hubs. */
const hubRoutes: RouteObject[] = HUBS.map((hub) =>
  hub.path === '/' ? { index: true, element: <HubPage key={hub.id} hubId={hub.id} /> } : { path: hub.path.slice(1), element: <HubPage key={hub.id} hubId={hub.id} /> },
);

/** Every former list URL opens its hub tab (the query is kept). */
const legacyRoutes: RouteObject[] = LEGACY_REDIRECTS.map((r) => ({ path: r.from.slice(1), element: <LegacyRedirect /> }));

/** Hubs that no longer exist (`/registry`): every tab opens where it moved. */
const removedHubRoutes: RouteObject[] = [...new Set(MOVED_TABS.map((m) => m.path))]
  .filter((path) => !HUBS.some((h) => h.path === path))
  .map((path) => ({ path: path.slice(1), element: <LegacyRedirect /> }));

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      {
        errorElement: <RouteErrorPage />,
        children: [
          ...hubRoutes,
          ...legacyRoutes,
          ...removedHubRoutes,
          { path: 'search', element: <SearchPage /> },
          { path: 'domains/:name/:tab?', element: <DomainDetailPage /> },
          { path: 'subjects/:id', element: <SubjectPage /> },
          { path: 'registry/transactions/:id', element: <RegistryTransactionPage /> },
          { path: 'ops/traces/:id', element: <TracePage /> },
          { path: 'ops/correlate/:id', element: <CorrelationPage /> },
          { path: 'compliance/icann/abuse/:id', element: <AbuseCasePage /> },
          { path: 'compliance/icann/disputes/:id', element: <DisputePage /> },
          { path: 'compliance/icann/disclosure/:id', element: <DisclosureCasePage /> },
          ...ACCOUNTS_ROUTES,
          ...COMMERCE_ROUTES,
          ...REGISTRY_ROUTES,
          ...MARKET_ROUTES,
          ...CORE_ROUTES,
          ...GOVERNANCE_ROUTES,
          ...ADMIN_ROUTES,
          ...INTEGRATIONS_ROUTES,
          ...OPS_ROUTES,
          ...WORK_ROUTES,
          ...SERVICES_ROUTES,
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
